import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, CheckCircle, AlertCircle, Eye, EyeOff, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

export default function AdminResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  
  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset from the admin login page.");
    }
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await apiClient.post("/auth/admin/reset-password", {
        token,
        new_password: password
      });
      
      setSuccess(true);
      toast.success("Admin password reset successful!");
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate("/bajrangbali");
      }, 3000);
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to reset password. Link may have expired.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  // Password strength indicator
  const getPasswordStrength = () => {
    if (!password) return { strength: 0, label: "", color: "" };
    
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[a-z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^A-Za-z0-9]/.test(password)) strength += 10;
    
    if (strength < 40) return { strength, label: "Weak", color: "bg-red-500" };
    if (strength < 70) return { strength, label: "Medium", color: "bg-yellow-500" };
    return { strength, label: "Strong", color: "bg-green-500" };
  };
  
  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen bg-viraly-dark flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-viraly-orange/20 to-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-viraly-orange" />
          </div>
          <h1 className="text-2xl font-bold text-viraly-light">Reset Admin Password</h1>
          <p className="text-viraly-muted mt-2">Create a new password for your admin account</p>
        </div>
        
        {error && !success && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}
        
        {success ? (
          <div className="card-base p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-viraly-light mb-2">Password Reset Complete!</h2>
            <p className="text-viraly-muted mb-4">
              Your admin password has been successfully updated.
            </p>
            <p className="text-sm text-viraly-muted mb-6">
              Redirecting to admin login...
            </p>
            <Link 
              to="/bajrangbali" 
              className="inline-flex items-center gap-2 text-viraly-orange hover:underline"
            >
              Go to Admin Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="card-base p-6 space-y-4">
            <div>
              <label className="text-sm text-viraly-light mb-2 block">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="input-base pl-10 pr-10"
                  required
                  disabled={!token}
                  data-testid="admin-reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-viraly-muted hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password strength indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-viraly-muted">Password strength</span>
                    <span className={`font-medium ${
                      passwordStrength.label === "Strong" ? "text-green-400" :
                      passwordStrength.label === "Medium" ? "text-yellow-400" : "text-red-400"
                    }`}>{passwordStrength.label}</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${passwordStrength.color} transition-all duration-300`}
                      style={{ width: `${passwordStrength.strength}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <label className="text-sm text-viraly-light mb-2 block">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="input-base pl-10"
                  required
                  disabled={!token}
                  data-testid="admin-reset-confirm-input"
                />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
              )}
            </div>
            
            <Button
              type="submit"
              disabled={loading || !token || password.length < 8 || password !== confirmPassword}
              className="btn-primary w-full"
              data-testid="admin-reset-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
            
            <p className="text-xs text-viraly-muted text-center mt-4">
              Password must be at least 8 characters
            </p>
          </form>
        )}
        
        <div className="text-center mt-6">
          <Link 
            to="/bajrangbali" 
            className="text-viraly-muted hover:text-white text-sm flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin Login
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
